import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isBefore } from 'date-fns';
import styled from 'styled-components';

import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { iconClock } from '@sorare/core/src/atoms/icons/system/iconClock.ts';
import { iconSignalStream } from '@sorare/core/src/atoms/icons/system/iconSignalStream.ts';
import { TextSwitch } from '@sorare/core/src/atoms/inputs/TextSwitch';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LeagueAvatar } from '@sorare/core/src/components/league/LeagueAvatar';
import { useTickerContext } from '@sorare/core/src/contexts/ticker';
import type { LeagueLaunch } from '@sorare/marketplace/src/lib/newSeason';
import {
  getLeagueAvatar,
  leagueLaunchAvatarInput,
} from '@sorare/marketplace/src/lib/newSeason';

import type {
  LeagueNavigator_competition,
  LeagueNavigator_so5Competition,
} from './__generated__/index.graphql';

const Root = styled(Vertical)`
  overflow: hidden;
  max-width: 100%;
`;

const LeagueContainer = styled(Horizontal).attrs({ gap: 1 })`
  padding: var(--half-unit);
  border-radius: var(--triple-unit);
  background: var(--c-nd-50);
  backdrop-filter: blur(32px);
`;

const StyledTextSwitch = styled(TextSwitch)`
  &.primary {
    --bar-border: none;
    --tab-padding-y: calc(var(--half-unit));
    --active-bg-color: var(--c-nd-300);
    --button-border-radius: 999px;
    --bar-border-radius: 999px;
  }
`;

const StyledLeagueAvatar = styled(LeagueAvatar)`
  width: var(--triple-unit);
  height: var(--triple-unit);
  object-fit: contain;
  object-position: center;
`;

const WithCheck = styled(Horizontal).attrs({ gap: 0.5 })`
  align-items: center;
  padding: 0 0 0 var(--unit);
  border-radius: var(--triple-unit);
  background: var(--c-nd-100);
`;

const Relative = styled(Horizontal).attrs({ gap: 0.5 })`
  position: relative;
  isolation: isolate;
`;

const Badge = styled.span`
  position: absolute;
  bottom: calc(var(--half-unit) * -1);
  right: calc(var(--half-unit) * -1);
  width: var(--double-unit);
  height: var(--double-unit);
  background: var(--c-nd-50);
  border-radius: var(--double-unit);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  &.live {
    background: var(--c-red-400);
  }
`;
const LiveIndicator = ({ startDate }: { startDate: Date }) => {
  const { now } = useTickerContext();
  if (isBefore(startDate, now)) {
    return (
      <Badge className="live">
        <FontAwesomeIcon icon={iconSignalStream} size="2xs" />
      </Badge>
    );
  }
  return (
    <Badge>
      <FontAwesomeIcon icon={iconClock} size="2xs" color="var(--c-nd-600)" />
    </Badge>
  );
};

type Props = {
  upcomingAndLiveLeagues: LeagueLaunch<
    LeagueNavigator_competition,
    LeagueNavigator_so5Competition
  >[];
  releasedLeagues: LeagueLaunch<
    LeagueNavigator_competition,
    LeagueNavigator_so5Competition
  >[];
  selectedLeague: string;
  onNavigateToLeague: (slug: string) => void;
  registerPillRef?: (slug: string, element: HTMLElement | null) => void;
};

export const LeagueNavigator = ({
  upcomingAndLiveLeagues,
  releasedLeagues,
  selectedLeague,
  onNavigateToLeague,
  registerPillRef,
}: Props) => {
  const upcomingSelectedLeague = upcomingAndLiveLeagues.find(
    l => l.slug === selectedLeague
  )?.slug;
  const releasedSelectedLeague = releasedLeagues.find(
    l => l.slug === selectedLeague
  )?.slug;

  const upcomingAndLiveLeaguesOptions = upcomingAndLiveLeagues.map(l => ({
    label: (
      <Relative as="span" ref={element => registerPillRef?.(l.slug, element)}>
        <LiveIndicator startDate={l.startDate} />
        <StyledLeagueAvatar
          league={getLeagueAvatar(leagueLaunchAvatarInput(l), 'icon')}
        />
      </Relative>
    ),
    value: l.slug,
  }));

  const releasedLeaguesOptions = releasedLeagues.map(l => ({
    label: (
      <span ref={element => registerPillRef?.(l.slug, element)}>
        <StyledLeagueAvatar
          league={getLeagueAvatar(leagueLaunchAvatarInput(l), 'icon')}
        />
      </span>
    ),
    value: l.slug,
  }));

  if (
    upcomingAndLiveLeaguesOptions.length === 0 &&
    releasedLeaguesOptions.length === 0
  ) {
    return null;
  }

  return (
    <Root>
      <FullViewportScroll>
        <LeagueContainer>
          {upcomingAndLiveLeaguesOptions.length > 0 && (
            <StyledTextSwitch<string>
              value={upcomingSelectedLeague}
              options={upcomingAndLiveLeaguesOptions}
              onChange={v => typeof v === 'string' && onNavigateToLeague(v)}
            />
          )}
          {releasedLeaguesOptions.length > 0 && (
            <WithCheck>
              <FontAwesomeIcon
                icon={iconCheck}
                color="var(--c-onBase-success)"
              />
              <StyledTextSwitch<string>
                value={releasedSelectedLeague}
                options={releasedLeaguesOptions}
                onChange={v => typeof v === 'string' && onNavigateToLeague(v)}
              />
            </WithCheck>
          )}
        </LeagueContainer>
      </FullViewportScroll>
    </Root>
  );
};

LeagueNavigator.fragments = {
  competition: gql`
    fragment LeagueNavigator_competition on Competition {
      slug
      ...getLeagueAvatar_competition
    }
    ${getLeagueAvatar.fragments.competition}
  ` as TypedDocumentNode<LeagueNavigator_competition>,
  so5Competition: gql`
    fragment LeagueNavigator_so5Competition on So5Competition {
      slug
      ...getLeagueAvatar_so5Competition
    }
    ${getLeagueAvatar.fragments.so5Competition}
  ` as TypedDocumentNode<LeagueNavigator_so5Competition>,
};
